import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Switch,
  IconButton,
  Button,
  CircularProgress,
  Tooltip,
  Alert,
  Snackbar,
} from '@mui/material';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import QrCode2OutlinedIcon from '@mui/icons-material/QrCode2Outlined';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import AttachMoneyOutlinedIcon from '@mui/icons-material/AttachMoneyOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import BankingService from 'services/banking.service';
import { IQRCode } from 'types/banking';
import { DateTime } from 'luxon';
import { useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BankingState } from 'stores/banking';
import { EAccountType } from 'common/accounts.interface';
import VMasker from 'vanilla-masker';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import ViewQRCodeModal from './ViewQRCodeModal';

interface PixKeysProps {
  open: boolean;
  onClose: () => void;
}

interface PixKeyItemProps {
  label: string;
  value?: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (val: boolean) => void;
  loading?: boolean;
  locked?: boolean;
  lockedBankName?: string;
}

interface QRCodeCardProps {
  qrCode: IQRCode;
  onView: (qrCode: IQRCode) => void;
  onDelete: (qrCode: IQRCode) => void;
}

const QRCodeCard = ({ qrCode, onView, onDelete }: QRCodeCardProps) => {
  return (
    <Box border="1px solid" borderColor="divider" borderRadius={3} p={3} mb={2} bgcolor="background.default">
      <Box display="flex" justifyContent="space-between" mb={2}>
        <InfoItem
          icon={<CalendarTodayOutlinedIcon />}
          label="Criado em:"
          value={DateTime.fromISO(qrCode.createdAt).toFormat('dd MMM yyyy')}
        />
        <InfoItem icon={<StarOutlineOutlinedIcon />} label="Tipo" value={qrCode.type === 'STATIC' ? 'Estático' : 'Dinâmico'} />
        <InfoItem
          icon={<AttachMoneyOutlinedIcon />}
          label="Valor"
          value={qrCode.amount ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(qrCode.amount) : '-'}
        />
      </Box>

      <Box display="flex" justifyContent="center" gap={2}>
        <Button
          variant="contained"
          onClick={() => onView(qrCode)}
          sx={{
            borderRadius: 999,
            px: 3,
            textTransform: 'none',
            width: '10vw',
          }}
        >
          Ver QRCode
        </Button>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteOutlineOutlinedIcon />}
          onClick={() => onDelete(qrCode)}
          sx={{
            borderRadius: 999,
            px: 3,
            textTransform: 'none',
            width: '10vw',
          }}
        >
          Excluir
        </Button>
      </Box>
    </Box>
  );
};

const InfoItem = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => {
  return (
    <Box display="flex" alignItems="center" gap={1}>
      {icon}
      <Box>
        <Typography fontSize={12} color="text.secondary">
          {label}
        </Typography>
        <Typography fontSize={13} fontWeight={600}>
          {value}
        </Typography>
      </Box>
    </Box>
  );
};

const PixKeyItem = ({ label, value, checked, disabled, onChange, loading, locked, lockedBankName }: PixKeyItemProps) => {
  const isDisabled = disabled || locked;
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (value) {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const content = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      border="1px solid"
      borderColor={locked ? 'warning.light' : 'divider'}
      borderRadius={2}
      px={2}
      py={1.5}
      mb={1.5}
      bgcolor={isDisabled ? 'background.default' : 'background.paper'}
      sx={{ opacity: locked ? 0.7 : 1 }}
    >
      <Box display="flex" alignItems="center" gap={1.5}>
        {locked && <LockOutlinedIcon sx={{ fontSize: 18, color: 'warning.main' }} />}
        <Box>
          <Typography fontSize={13} fontWeight={600}>
            {label}
          </Typography>

          {value && (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography fontSize={13} color="text.secondary">
                {value}
              </Typography>
              {checked && (
                <Tooltip title={copied ? 'Copiado!' : 'Copiar'} placement="top">
                  <IconButton size="small" onClick={handleCopy}>
                    <ContentCopyOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {loading ? <CircularProgress size={20} /> : <Switch checked={checked} disabled={isDisabled} onChange={(_, val) => onChange?.(val)} />}
    </Box>
  );

  if (locked && lockedBankName) {
    return (
      <Tooltip title={`Chave já registrada no banco ${lockedBankName}`} arrow placement="top">
        {content}
      </Tooltip>
    );
  }

  return content;
};

const PixKeys = ({ open, onClose }: PixKeysProps) => {
  const [qrcodes, setQrcodes] = useState<IQRCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [keysLoading, setKeysLoading] = useState(false);
  const [checkingKeysAvailability, setCheckingKeysAvailability] = useState(false);
  const [viewQrCode, setViewQrCode] = useState<IQRCode | null>(null);
  const [deleteQrCode, setDeleteQrCode] = useState<IQRCode | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });
  const bankingService = new BankingService();

  const { accounts } = useSelector<IRootState, BankingState>(({ banking }) => banking);
  const account = accounts.find((acc) => acc.accountType === EAccountType.PAYMENT) || accounts[0];

  const [pixKeys, setPixKeys] = useState<{
    pixKeyEvp: string | null;
    pixKeyDoc: string | null;
    pixKeyEmail: string | null;
  }>({
    pixKeyEvp: null,
    pixKeyDoc: null,
    pixKeyEmail: null,
  });

  const [lockedKeys, setLockedKeys] = useState<{
    cnpj: string | null;
    email: string | null;
  }>({
    cnpj: null,
    email: null,
  });

  const CACHE_DURATION = 4 * 60 * 60 * 1000; // 4 hours in milliseconds
  const CACHE_KEY = 'pix-keys-availability-cache';

  const getCachedKeyInfo = (pixKey: string): string | null | undefined => {
    try {
      const cache = localStorage.getItem(CACHE_KEY);
      if (!cache) return undefined;

      const parsed = JSON.parse(cache);
      const keyData = parsed[pixKey];

      if (!keyData) return undefined;

      // Check if cache is still valid (less than 4 hours old)
      const now = Date.now();
      if (now - keyData.timestamp < CACHE_DURATION) {
        return keyData.bankName;
      }

      return undefined;
    } catch {
      return undefined;
    }
  };

  const setCachedKeyInfo = (pixKey: string, bankName: string | null) => {
    try {
      const cache = localStorage.getItem(CACHE_KEY);
      const parsed = cache ? JSON.parse(cache) : {};

      parsed[pixKey] = {
        bankName,
        timestamp: Date.now(),
      };

      localStorage.setItem(CACHE_KEY, JSON.stringify(parsed));
    } catch {
      // Ignore cache errors
    }
  };

  const fetchQRCodes = async () => {
    try {
      setLoading(true);
      const data = await bankingService.listQRCodes();
      setQrcodes(data.filter((qr) => !!qr.baasTransactionId && !!qr.emvqrcps));
    } catch {
      // Ignore fetch error
    } finally {
      setLoading(false);
    }
  };

  const fetchPixKeys = async () => {
    try {
      setKeysLoading(true);
      const data = await bankingService.getPixKeys();
      setPixKeys(data);
    } catch {
      // Ignore fetch error
    } finally {
      setKeysLoading(false);
    }
  };

  const checkPixKeyAvailability = async (pixKey: string, keyType: 'cnpj' | 'email') => {
    // Check cache first
    const cachedResult = getCachedKeyInfo(pixKey);
    if (cachedResult !== undefined) {
      // Use cached result (can be null for available or string for locked)
      setLockedKeys((prev) => ({
        ...prev,
        [keyType]: cachedResult,
      }));
      return;
    }

    try {
      const response = await bankingService.getPixKeyInfo({
        pixKey,
        accountNumber: account?.bankAccount || '',
      });

      // If response has bankName, the key is registered in another bank
      const bankName = response?.bankName || null;
      setLockedKeys((prev) => ({
        ...prev,
        [keyType]: bankName,
      }));

      // Cache the result
      setCachedKeyInfo(pixKey, bankName);
    } catch {
      // If error, key is available (not found means not registered)
      setLockedKeys((prev) => ({
        ...prev,
        [keyType]: null,
      }));

      // Cache the result (null means available)
      setCachedKeyInfo(pixKey, null);
    }
  };

  useEffect(() => {
    if (open) {
      fetchQRCodes();
      fetchPixKeys();
    }
  }, [open]);

  useEffect(() => {
    const checkAvailability = async () => {
      if (open && account) {
        const shouldCheckCnpj = !pixKeys.pixKeyDoc && account.cnpj;
        const shouldCheckEmail = !pixKeys.pixKeyEmail && account.email;

        if (shouldCheckCnpj || shouldCheckEmail) {
          setCheckingKeysAvailability(true);

          try {
            // Check CNPJ availability if not active (will use cache if available)
            if (shouldCheckCnpj) {
              await checkPixKeyAvailability(account.cnpj, 'cnpj');
            }

            // Check Email availability if not active (will use cache if available)
            if (shouldCheckEmail) {
              await checkPixKeyAvailability(account.email, 'email');
            }
          } finally {
            setCheckingKeysAvailability(false);
          }
        }
      }
    };

    checkAvailability();
  }, [open, pixKeys.pixKeyDoc, pixKeys.pixKeyEmail, account]);

  const handleToggleKey = async (type: 'CPF' | 'CNPJ' | 'EMAIL' | 'EVP', currentKey: string | null, valueToCreate: string) => {
    try {
      setKeysLoading(true);
      let response: any;

      if (currentKey) {
        // Delete
        response = await bankingService.requestDeletePixKey(currentKey, type);
      } else {
        // Create
        response = await bankingService.requestCreatePixKey({ keyType: type, key: valueToCreate });
      }

      // Check if operation was successful
      const isSuccess = response?.status === 'SUCCESS' || response?.status === 'CONFIRMED';

      if (isSuccess) {
        await fetchPixKeys();
        setSnackbar({
          open: true,
          message: currentKey ? 'Chave PIX desativada com sucesso!' : 'Chave PIX ativada com sucesso!',
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: response?.message || 'Erro ao processar chave PIX. Tente novamente.',
          severity: 'error',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Erro ao processar chave PIX. Tente novamente.',
        severity: 'error',
      });
    } finally {
      setKeysLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteQrCode?.id) return;

    try {
      setLoading(true);
      if (deleteQrCode.type === 'STATIC') {
        await bankingService.deleteStaticQRCode(deleteQrCode.id);
      } else {
        await bankingService.deleteDynamicQRCode(deleteQrCode.id);
      }
      setDeleteQrCode(null);
      await fetchQRCodes();
    } catch (error) {
      // Ignore error
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4, position: 'relative' }}>
          <IconButton
            onClick={onClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500',
            }}
          >
            <CloseIcon />
          </IconButton>

          <Box display="flex" alignItems="center" justifyContent="center" gap={1} mb={3}>
            <QrCode2OutlinedIcon color="primary" />
            <Typography fontWeight={600} fontSize={18} textAlign="center">
              Minhas chaves PIX
            </Typography>
          </Box>

          <Typography fontWeight={600} mb={0.5}>
            Chave PIX
          </Typography>
          <Typography fontSize={13} color="text.secondary" mb={2}>
            Habilite a Chave PIX para uso nessa conta
          </Typography>

          {checkingKeysAvailability ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={8}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <PixKeyItem
                label="CNPJ"
                value={VMasker.toPattern(account?.cnpj || '', '99.999.999/9999-99')}
                checked={!!pixKeys.pixKeyDoc}
                onChange={() => handleToggleKey('CNPJ', pixKeys.pixKeyDoc, account?.cnpj || '')}
                loading={keysLoading}
                locked={!!lockedKeys.cnpj}
                lockedBankName={lockedKeys.cnpj || undefined}
              />

              <PixKeyItem
                label="E-mail"
                value={account?.email}
                checked={!!pixKeys.pixKeyEmail}
                onChange={() => handleToggleKey('EMAIL', pixKeys.pixKeyEmail, account?.email || '')}
                loading={keysLoading}
                locked={!!lockedKeys.email}
                lockedBankName={lockedKeys.email || undefined}
              />

              <PixKeyItem
                label="Chave aleatória"
                value={pixKeys.pixKeyEvp || undefined}
                checked={!!pixKeys.pixKeyEvp}
                onChange={() => handleToggleKey('EVP', pixKeys.pixKeyEvp, '')}
                loading={keysLoading}
              />
            </>
          )}

          <Box mt={4}>
            <Typography fontWeight={600} mb={2}>
              QRCode ativos
            </Typography>

            {loading && !deleteQrCode ? (
              <Box display="flex" justifyContent="center" p={3}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {qrcodes.map((qr) => (
                  <QRCodeCard key={qr.id} qrCode={qr} onView={(item) => setViewQrCode(item)} onDelete={(item) => setDeleteQrCode(item)} />
                ))}
                {qrcodes.length === 0 && (
                  <Typography color="text.secondary" textAlign="center">
                    Nenhum QRCode ativo
                  </Typography>
                )}
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <ViewQRCodeModal open={!!viewQrCode} onClose={() => setViewQrCode(null)} qrCode={viewQrCode} />

      <ConfirmDeleteModal
        open={!!deleteQrCode}
        onClose={() => setDeleteQrCode(null)}
        onConfirm={handleDeleteConfirm}
        title="Excluir QRCode"
        description="Tem certeza que deseja excluir este QRCode? O código deixará de funcionar imediatamente."
        loading={loading}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PixKeys;
